import React, { useState } from 'react';

interface CommentComposerProps {
  /** Hide the "Add a comment" label for inline reply composers. */
  variant?: 'top-level' | 'reply';
  placeholder?: string;
  submitLabel?: string;
  /** When false, composer is rendered disabled with a hint. */
  enabled: boolean;
  /** Called with trimmed body. Returning a promise lets us await success. */
  onSubmit: (body: string) => Promise<void> | void;
  onCancel?: () => void;
  /** Pending state from the parent mutation. */
  isPending: boolean;
}

const MAX_BODY = 10000;
const COUNTER_THRESHOLD = MAX_BODY * 0.9; // show counter only near the cap

export default function CommentComposer({
  variant = 'top-level',
  placeholder = 'What do you think about the course?',
  submitLabel = 'Post Comment',
  enabled,
  onSubmit,
  onCancel,
  isPending,
}: CommentComposerProps) {
  const [body, setBody] = useState('');
  const trimmed = body.trim();
  const tooLong = body.length > MAX_BODY;
  const canSubmit = enabled && !isPending && trimmed.length > 0 && !tooLong;
  const showCount = body.length >= COUNTER_THRESHOLD;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onSubmit(trimmed);
    setBody('');
  };

  const handleClear = () => {
    setBody('');
    onCancel?.();
  };

  const isTopLevel = variant === 'top-level';

  return (
    <div className="space-y-2">
      {isTopLevel && (
        <h3 className="text-lg font-medium text-text-primary tracking-tight">
          Add a comment
        </h3>
      )}
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder}
        disabled={!enabled}
        rows={isTopLevel ? 4 : 3}
        maxLength={MAX_BODY + 1}
        className="block w-full rounded-lg border border-transparent bg-surface-primary px-3 py-2 text-sm leading-5 text-text-primary placeholder-text-tertiary resize-y min-h-[80px] focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 disabled:opacity-60"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="inline-flex items-center justify-center h-9 px-4 rounded-full bg-primary-500 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-600 dark:hover:bg-primary-400 active:scale-[0.97] transition-[background-color,transform] duration-fast ease-out-strong"
        >
          {isPending ? 'Posting…' : submitLabel}
        </button>
        <button
          type="button"
          onClick={handleClear}
          disabled={isPending || (body.length === 0 && !onCancel)}
          className="inline-flex items-center justify-center h-9 px-4 rounded-full text-sm font-medium text-text-secondary opacity-70 hover:opacity-100 hover:bg-[rgba(3,2,19,0.04)] dark:hover:bg-white/[0.04] active:scale-[0.97] disabled:opacity-40 disabled:active:scale-100 transition-[background-color,transform,opacity] duration-fast ease-out-strong"
        >
          {variant === 'reply' ? 'Cancel' : 'Clear'}
        </button>
        {showCount && (
          <span
            className={`ml-auto text-xs tabular-nums ${
              tooLong ? 'text-error-500' : 'text-text-tertiary'
            }`}
          >
            {body.length} / {MAX_BODY}
          </span>
        )}
      </div>
    </div>
  );
}
